import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch } from "app/hooks";
import {
  MODAL_SECTIONS,
  MODAL_TYPES,
  openModal,
  SESSION_MODAL_TYPES,
} from "app/reducers/modalSlice";
import classNames from "classnames";
import { NAVIGATOR } from "features/navigation";
import { SESSION_STATUS } from "interfaces/Sessions";
import { useHistory } from "react-router";
import { BUTTON_STYLES, BUTTON_TYPES, Spinner } from "ui";

const Buttons = ({
  sessionId,
  configId,
  status,
  disabledStartMatch,
  isLoading,
  showWarningMsg,
  isActionLoading,
  handleStartSession,
  handleStartGame,
  hasLowBatteryHeadsets,
}: {
  sessionId?: string;
  configId: string;
  status?: SESSION_STATUS;
  disabledStartMatch?: boolean;
  isLoading?: boolean;
  showWarningMsg?: boolean;
  isActionLoading: boolean;
  handleStartSession: (configId: string) => Promise<void>;
  handleStartGame: (sessionId: string) => Promise<void>;
  hasLowBatteryHeadsets?: boolean;
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  switch (status) {
    case SESSION_STATUS.STANDBY:
      return (
        <div className="flex justify-end mt-[10px] mb-3 gap-3">
          {showWarningMsg && (
            <div className="flex items-center w-96">
              <WarningAmberIcon
                fontSize="large"
                className="text-yellow-400"
                aria-hidden="true"
              />
              <p className="ml-2 text-s">
                Para iniciar la partida, asegúrese de que al menos un casco esté
                conectado
              </p>
            </div>
          )}
          <MUIButton
            onClick={() => {
              dispatch(
                openModal({
                  type: MODAL_TYPES.CONFIRMATION,
                  section: MODAL_SECTIONS.SESSION,
                  sessionId,
                  extraData: {
                    sessionModalType: SESSION_MODAL_TYPES.CANCEL,
                  },
                }),
              );
            }}
            className={classNames("h-12 w-52 border-2")}
            variant="outlined"
          >
            {isActionLoading || isLoading ? (
              <Spinner color="white" />
            ) : (
              <p>Cancelar Sesión</p>
            )}
          </MUIButton>
          <MUIButton
            variant="contained"
            disabled
            onClick={() => null}
            className={classNames("h-12 w-52")}
          >
            {isActionLoading || isLoading ? (
              <Spinner color="white" />
            ) : (
              <p>Iniciar Partida</p>
            )}
          </MUIButton>
        </div>
      );
    case SESSION_STATUS.READY:
      return (
        <div className="flex justify-end mt-[10px] gap-3">
          {showWarningMsg && (
            <div className="flex items-center w-96">
              <WarningAmberIcon
                fontSize="large"
                className="text-yellow-400"
                aria-hidden="true"
              />
              <p className="ml-2 text-s">
                Para iniciar la partida, asegúrese de que al menos un casco esté
                conectado
              </p>
            </div>
          )}
          <MUIButton
            onClick={() => {
              dispatch(
                openModal({
                  type: MODAL_TYPES.CONFIRMATION,
                  section: MODAL_SECTIONS.SESSION,
                  sessionId,
                  extraData: {
                    sessionModalType: SESSION_MODAL_TYPES.CANCEL,
                  },
                }),
              );
            }}
            className={classNames("h-12 w-52 border-2")}
            variant="outlined"
          >
            {isActionLoading || isLoading ? (
              <Spinner color="white" />
            ) : (
              <p>Cancelar Sesión</p>
            )}
          </MUIButton>
          <MUIButton
            variant="contained"
            disabled={disabledStartMatch}
            onClick={() => {
              if (hasLowBatteryHeadsets) {
                dispatch(
                  openModal({
                    type: MODAL_TYPES.CONFIRMATION,
                    section: MODAL_SECTIONS.SESSION,
                    sessionId,
                    extraData: {
                      sessionModalType: SESSION_MODAL_TYPES.LOW_BATTERY,
                    },
                  }),
                );
              } else {
                handleStartGame(sessionId || "");
              }
            }}
            className={classNames("h-12 w-52")}
          >
            {isActionLoading || isLoading ? (
              <Spinner color="white" />
            ) : (
              <p>Iniciar Partida</p>
            )}
          </MUIButton>
        </div>
      );
    case SESSION_STATUS.IN_PROGRESS:
      return (
        <div className="flex justify-end mt-[10px]">
          <MUIButton
            variant="contained"
            onClick={() => history.push(`${NAVIGATOR.sessions}/${sessionId}`)}
            className={classNames(
              "h-12 w-52 mr-3 mt-[10px]",
              BUTTON_STYLES[BUTTON_TYPES.CANCEL],
            )}
          >
            {isActionLoading || isLoading ? <Spinner /> : <p>Ver Sesión</p>}
          </MUIButton>
          <MUIButton
            variant="contained"
            onClick={() => {
              dispatch(
                openModal({
                  type: MODAL_TYPES.CONFIRMATION,
                  section: MODAL_SECTIONS.SESSION,
                  sessionId,
                  extraData: {
                    sessionModalType: SESSION_MODAL_TYPES.FINALIZE,
                  },
                }),
              );
            }}
            className={classNames(
              "h-12 w-52 mt-[10px]",
              BUTTON_STYLES[BUTTON_TYPES.AUTH],
            )}
          >
            {isActionLoading || isLoading ? (
              <Spinner color="white" />
            ) : (
              <p>Finalizar partida(s)</p>
            )}
          </MUIButton>
        </div>
      );
    default:
      return (
        <div className="flex justify-end mt-[10px] mb-3">
          <MUIButton
            variant="contained"
            onClick={() => handleStartSession(configId)}
            className={classNames(
              "h-12 w-52",
              BUTTON_STYLES[BUTTON_TYPES.AUTH],
            )}
          >
            {isActionLoading || isLoading ? (
              <Spinner color="white" />
            ) : (
              <p>Comenzar Sesión</p>
            )}
          </MUIButton>
        </div>
      );
  }
};

export default Buttons;
